import React, { useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { translations } from '../i18n';
import LanguageSelector from './LanguageSelector';

const RegistrationForm = ({ onSuccess, currentLanguage, onLanguageChange }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    practitionerName: '',
    clinicName: '',
    clinicAddress: '',
    clinicCity: '',
    clinicPostalCode: '',
    clinicPhone: '',
    clinicSize: '',
    demoPreference: false,
    language: currentLanguage
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const t = translations[currentLanguage];

  // Update language in form data when language changes
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      language: currentLanguage
    }));
  }, [currentLanguage]);

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '48px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#667eea',
      },
      borderColor: state.isFocused ? '#667eea' : errors.clinicSize ? '#ef4444' : '#e5e7eb',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 12px',
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      padding: '0 12px',
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '8px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      border: '1px solid #e5e7eb',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#667eea' 
        : state.isFocused 
          ? '#f3f4f6' 
          : 'white',
      color: state.isSelected ? 'white' : '#374151',
      padding: '12px 16px',
      fontSize: '16px',
      fontWeight: '500',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#374151',
      fontSize: '16px',
      fontWeight: '500',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
      fontSize: '16px',
    }),
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = t.required;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t.emailInvalid;
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = t.required;
    } else {
      const password = formData.password;
      const hasMinLength = password.length >= 8;
      const hasUpperCase = /[A-Z]/.test(password);
      const hasLowerCase = /[a-z]/.test(password);
      const hasNumber = /[0-9]/.test(password);
      const hasSpecialChar = /[!@#$%^&*]/.test(password);
      
      if (!hasMinLength) {
        newErrors.password = t.passwordMinLength;
      } else if (!hasUpperCase) {
        newErrors.password = t.passwordNoUpperCase;
      } else if (!hasLowerCase) {
        newErrors.password = t.passwordNoLowerCase;
      } else if (!hasNumber) {
        newErrors.password = t.passwordNoNumber;
      } else if (!hasSpecialChar) {
        newErrors.password = t.passwordNoSpecialChar;
      }
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t.required;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t.passwordsNotMatch;
    }

    // Practitioner name validation
    if (!formData.practitionerName.trim()) {
      newErrors.practitionerName = t.required;
    }

    // Clinic name validation
    if (!formData.clinicName.trim()) {
      newErrors.clinicName = t.required;
    }

    // Clinic address validation
    if (!formData.clinicAddress.trim()) {
      newErrors.clinicAddress = t.required;
    }

    // Clinic city validation
    if (!formData.clinicCity.trim()) {
      newErrors.clinicCity = t.required;
    }

    // Postal code validation
    if (!formData.clinicPostalCode.trim()) {
      newErrors.clinicPostalCode = t.required;
    } else if (!/^\d{4}$/.test(formData.clinicPostalCode)) {
      newErrors.clinicPostalCode = t.postalCodeInvalid;
    }

    // Phone validation
    if (!formData.clinicPhone.trim()) {
      newErrors.clinicPhone = t.required;
    }

    // Clinic size validation
    if (!formData.clinicSize) {
      newErrors.clinicSize = t.required;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear errors when user starts typing
    if (errors[name] || errors.submit) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
        submit: '' // Also clear general submit errors
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.post(`${apiBaseUrl}/api/registration`, formData);
      
      if (response.status === 201) {
        onSuccess();
      }
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      if (error.response?.status === 400) {
        // Handle validation errors from server
        const serverErrors = error.response.data.errors || {};
        const serverError = error.response.data.error;
        console.log('Server errors:', serverErrors);
        console.log('Server error:', serverError);
        
        // Translate error messages if they exist
        const translatedErrors = {};
        let hasEmailError = false;
        
        // Check for email error in both errors object and main error field
        if (serverError === 'EMAIL_ALREADY_EXISTS' || serverErrors.email === 'EMAIL_ALREADY_USED') {
          translatedErrors.email = t.emailAlreadyUsed;
          translatedErrors.submit = t.emailAlreadyExists;
          hasEmailError = true;
          console.log('Email error detected, setting translated messages');
        } else {
          // Handle other field errors
          Object.keys(serverErrors).forEach(key => {
            console.log(`Processing error key: ${key}, value: ${serverErrors[key]}`);
            translatedErrors[key] = serverErrors[key];
          });
        }
        
        console.log('Final translated errors:', translatedErrors);
        setErrors(translatedErrors);
      } else {
        // Handle other errors
        console.log('Setting general submit error:', t.submitError);
        setErrors({ 
          submit: t.submitError
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="form-container">
      <LanguageSelector 
        currentLanguage={currentLanguage} 
        onLanguageChange={onLanguageChange} 
      />
          <h1 className="form-title">{t.title}</h1>
          <p className="form-subtitle">
            <strong>{t.subtitle}</strong>
          </p>


          <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              {t.email} <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder={t.emailPlaceholder}
            />
            {errors.email && <div className="error-message">{errors.email}</div>}
          </div>
        </div>

        <div className="form-row password-row">
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              {t.password} <span className="required">*</span>
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={`form-input ${errors.password ? 'error' : ''}`}
              placeholder={t.passwordPlaceholder}
            />
            {errors.password && <div className="error-message">{errors.password}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              {t.confirmPassword} <span className="required">*</span>
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
              placeholder={t.confirmPasswordPlaceholder}
            />
            {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="practitionerName" className="form-label">
              {t.practitionerName} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="practitionerName"
              name="practitionerName"
              value={formData.practitionerName}
              onChange={handleInputChange}
              className={`form-input ${errors.practitionerName ? 'error' : ''}`}
              placeholder={t.practitionerNamePlaceholder}
            />
            {errors.practitionerName && <div className="error-message">{errors.practitionerName}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="clinicName" className="form-label">
              {t.clinicName} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="clinicName"
              name="clinicName"
              value={formData.clinicName}
              onChange={handleInputChange}
              className={`form-input ${errors.clinicName ? 'error' : ''}`}
              placeholder={t.clinicNamePlaceholder}
            />
            {errors.clinicName && <div className="error-message">{errors.clinicName}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="clinicAddress" className="form-label">
              {t.clinicAddress} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="clinicAddress"
              name="clinicAddress"
              value={formData.clinicAddress}
              onChange={handleInputChange}
              className={`form-input ${errors.clinicAddress ? 'error' : ''}`}
              placeholder={t.clinicAddressPlaceholder}
            />
            {errors.clinicAddress && <div className="error-message">{errors.clinicAddress}</div>}
          </div>
        </div>

        <div className="form-row postal-city-row">
          <div className="form-group postal-code-group">
            <label htmlFor="clinicPostalCode" className="form-label">
              {t.clinicPostalCode} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="clinicPostalCode"
              name="clinicPostalCode"
              value={formData.clinicPostalCode}
              onChange={handleInputChange}
              className={`form-input ${errors.clinicPostalCode ? 'error' : ''}`}
              placeholder={t.clinicPostalCodePlaceholder}
              maxLength="4"
            />
            {errors.clinicPostalCode && <div className="error-message">{errors.clinicPostalCode}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="clinicCity" className="form-label">
              {t.clinicCity} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="clinicCity"
              name="clinicCity"
              value={formData.clinicCity}
              onChange={handleInputChange}
              className={`form-input ${errors.clinicCity ? 'error' : ''}`}
              placeholder={t.clinicCityPlaceholder}
            />
            {errors.clinicCity && <div className="error-message">{errors.clinicCity}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="clinicPhone" className="form-label">
              {t.clinicPhone} <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="clinicPhone"
              name="clinicPhone"
              value={formData.clinicPhone}
              onChange={handleInputChange}
              className={`form-input ${errors.clinicPhone ? 'error' : ''}`}
              placeholder={t.clinicPhonePlaceholder}
            />
            {errors.clinicPhone && <div className="error-message">{errors.clinicPhone}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="clinicSize" className="form-label">
              {t.clinicSize} <span className="required">*</span>
            </label>
            <Select
              value={formData.clinicSize ? { value: formData.clinicSize, label: t.clinicSizeOptions[formData.clinicSize] } : null}
              onChange={(option) => setFormData({ ...formData, clinicSize: option ? option.value : '' })}
              options={[
                { value: '1', label: t.clinicSizeOptions['1'] },
                { value: '2-5', label: t.clinicSizeOptions['2-5'] },
                { value: '6-15', label: t.clinicSizeOptions['6-15'] }
              ]}
              styles={customSelectStyles}
              isSearchable={false}
              placeholder={t.clinicSize}
              className="clinic-size-select"
              classNamePrefix="clinic-size-select"
            />
            {errors.clinicSize && <div className="error-message">{errors.clinicSize}</div>}
          </div>
        </div>

        <div className="form-checkbox">
          <input
            type="checkbox"
            id="demoPreference"
            name="demoPreference"
            checked={formData.demoPreference}
            onChange={handleInputChange}
          />
          <label htmlFor="demoPreference">
            {t.demoPreference}
          </label>
        </div>


        <button
          type="submit"
          className="submit-button"
          disabled={isSubmitting}
        >
          {isSubmitting && <span className="loading"></span>}
          {isSubmitting ? t.submitting : t.submit}
        </button>

        {/* Error banner after submit button */}
        {console.log('Current errors state:', errors)}
        {errors.submit && (
          <div className="error-banner">
            <div className="error-banner-content">
              <span className="error-icon">⚠️</span>
              <span className="error-text">{errors.submit}</span>
            </div>
          </div>
        )}
      </form>

      <div className="help-section">
        <div className="help-title">{t.helpTitle}</div>
        <div className="help-text">
          {t.helpDescription}
        </div>
        <div className="help-text">
          📧 <a href={`mailto:${t.helpEmail}`} className="help-contact">{t.helpEmail}</a>
        </div>
        <div className="help-text">
          📞 <a href={`tel:+41764081208`} className="help-contact">{t.helpPhone}</a>
        </div>
      </div>
    </div>
  );
};

export default RegistrationForm;
